/**
 * Parsers for non-Journey H2 sections in `.canary.md` specs.
 *
 * The Journey parser remains in parse.ts because it is a state machine over
 * lead-ins, nested condition blocks, blockquotes, and sub-bullets. These
 * section parsers are independent Markdown/YAML list readers.
 *
 * @module spec/parse-sections
 */

import yaml from 'yaml';
import type { AttachmentDecl, DeclaredOutput, InputDecl, Scenario, SpecIssue } from './grammar.js';

export function parseInputs(body: string, startLine: number, issues: SpecIssue[]): InputDecl[] {
  const out: InputDecl[] = [];
  for (const { line, lineNo } of bulletLines(body, startLine)) {
    // `- credential <name>: <value>` or `- <name>: <value>`
    const credMatch = /^credential\s+([A-Za-z0-9_-]+)\s*:\s*(.+)$/.exec(line);
    if (credMatch) {
      out.push({ name: credMatch[1], kind: 'credential', defaultValue: credMatch[2].trim() });
      continue;
    }
    const m = /^([A-Za-z0-9_-]+)\s*:\s*(.+)$/.exec(line);
    if (!m) {
      issues.push({
        severity: 'warn',
        message: `Could not parse Inputs entry: "${line}"`,
        line: lineNo,
      });
      continue;
    }
    out.push({ name: m[1], kind: 'value', defaultValue: stripQuotes(m[2].trim()) });
  }
  return out;
}

export function parseScenarios(body: string, startLine: number, issues: SpecIssue[]): Scenario[] {
  const out: Scenario[] = [];
  const blocks = splitH3Blocks(body, startLine);
  for (const block of blocks) {
    const headingRaw = block.heading;
    // Strip *(default)* and *(skip: ...)* annotations.
    let isDefault = false;
    let includeInSuite = true;
    let skipReason: string | undefined;
    let nameClean = headingRaw;
    const defaultMatch = /\*\(default\)\*/i.exec(headingRaw);
    if (defaultMatch) {
      isDefault = true;
      nameClean = nameClean.replace(defaultMatch[0], '').trim();
    }
    const skipMatch = /\*\(skip(?::\s*([^)]+))?\)\*/i.exec(headingRaw);
    if (skipMatch) {
      includeInSuite = false;
      skipReason = (skipMatch[1] ?? '').trim() || undefined;
      nameClean = nameClean.replace(skipMatch[0], '').trim();
    }

    let bodyYaml: Record<string, unknown> = {};
    try {
      // Convert top-level bullets to YAML mapping by stripping the leading "- ".
      const content = block.body
        .split('\n')
        .map((line) => line.replace(/^-\s+/, ''))
        .join('\n');
      bodyYaml = (yaml.parse(content) ?? {}) as Record<string, unknown>;
    } catch (err) {
      issues.push({
        severity: 'warn',
        message: `Could not parse scenario "${nameClean}": ${err instanceof Error ? err.message : String(err)}`,
        line: block.startLine,
      });
    }

    out.push({
      name: nameClean,
      description: typeof bodyYaml.description === 'string' ? bodyYaml.description : undefined,
      isDefault,
      includeInSuite,
      skipReason,
      values: (bodyYaml.values ?? {}) as Record<string, string>,
      credentials: (bodyYaml.credentials ?? {}) as Record<string, string>,
    });
  }
  return out;
}

export function parseAttachments(
  body: string,
  startLine: number,
  issues: SpecIssue[]
): AttachmentDecl[] {
  const out: AttachmentDecl[] = [];
  for (const { line, lineNo } of bulletLines(body, startLine)) {
    const m = /^([A-Za-z0-9_-]+)\s*:\s*(.+)$/.exec(line);
    if (!m) {
      issues.push({
        severity: 'warn',
        message: `Could not parse Attachments entry: "${line}"`,
        line: lineNo,
      });
      continue;
    }
    out.push({ ref: m[1], source: m[2].trim() });
  }
  return out;
}

export function parseDeclaredOutputs(
  body: string,
  startLine: number,
  issues: SpecIssue[]
): DeclaredOutput[] {
  // Each top-level bullet introduces a dataKey; indented sub-bullets carry
  // optional `label:`, `property:`, `description:` fields. We round-trip
  // through YAML so quoted values work without bespoke escape handling.
  const out: DeclaredOutput[] = [];
  const lines = body.split('\n');
  let i = 0;
  while (i < lines.length) {
    const m = /^-\s+([A-Za-z0-9_-]+)\s*$/.exec(lines[i].trim());
    if (!m) {
      i++;
      continue;
    }
    const dataKey = m[1];
    const block: string[] = [];
    i++;
    while (i < lines.length) {
      const raw = lines[i];
      // Sub-bullets must be indented; a non-indented bullet starts a new entry.
      if (/^-\s/.test(raw)) break;
      const sub = /^\s+-\s+(.+)$/.exec(raw);
      if (sub) block.push(sub[1]);
      else if (raw.trim() === '') {
        // blank line - keep scanning, allows visual spacing inside an entry.
      } else {
        break;
      }
      i++;
    }
    let entry: Record<string, unknown> = {};
    if (block.length > 0) {
      try {
        entry = (yaml.parse(block.join('\n')) ?? {}) as Record<string, unknown>;
      } catch (err) {
        issues.push({
          severity: 'warn',
          message: `Could not parse Declared Outputs entry "${dataKey}": ${err instanceof Error ? err.message : String(err)}`,
          line: startLine,
        });
      }
    }
    out.push({
      dataKey,
      label: typeof entry.label === 'string' ? entry.label : undefined,
      // Spec writes `property: <name>`; the AST uses `propertyName` to make
      // the name-vs-id distinction explicit (sync.ts resolves to id at compile).
      propertyName: typeof entry.property === 'string' ? entry.property : undefined,
      description: typeof entry.description === 'string' ? entry.description : undefined,
    });
  }
  return out;
}

function bulletLines(body: string, startLine: number): { line: string; lineNo: number }[] {
  const out: { line: string; lineNo: number }[] = [];
  const lines = body.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('- ')) {
      out.push({ line: trimmed.slice(2).trim(), lineNo: startLine + i });
    }
  }
  return out;
}

function splitH3Blocks(
  body: string,
  startLine: number
): { heading: string; body: string; startLine: number }[] {
  const out: { heading: string; body: string; startLine: number }[] = [];
  const lines = body.split('\n');
  let current: { heading: string; body: string[]; startLine: number } | null = null;
  for (let i = 0; i < lines.length; i++) {
    const m = /^###\s+(.+?)\s*$/.exec(lines[i]);
    if (m) {
      if (current) {
        out.push({
          heading: current.heading,
          body: current.body.join('\n'),
          startLine: current.startLine,
        });
      }
      current = { heading: m[1], body: [], startLine: startLine + i };
      continue;
    }
    if (current) current.body.push(lines[i]);
  }
  if (current) {
    out.push({
      heading: current.heading,
      body: current.body.join('\n'),
      startLine: current.startLine,
    });
  }
  return out;
}

function stripQuotes(s: string): string {
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  return s;
}
